import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { Router } from "next/router";
import { Flex, Text } from "@chakra-ui/react";
import { isEmpty } from "lodash";
import LogRocket from "logrocket";
import { OrangeButton } from "@/lib/chakra/components";
import { useOnlineStatus } from "@/lib/customHooks";
import { inter, lato, openSans, roboto } from "@/lib/fonts";
import { useTokenRefreshQuery } from "@/lib/services/appApi";
import { useAppDispatch, useAppSelector } from "../../lib/hooks";
import { setAccessToken, setAuthState } from "../../lib/reducers/user";
import { Loader } from "../utils/Loader";
import DataLoader from "./DataLoader";

interface Props {
  children: ReactNode;
  router: Router;
}

export const RootContext = createContext<
  | {
      ctxTitle: string | JSX.Element;
      defaultFont: string;
      setCtxTitle: Dispatch<SetStateAction<string | JSX.Element>> | undefined;
      setDefaultFont?: Dispatch<SetStateAction<string>>;
    }
  | undefined
>(undefined);

const AuthWrapper = (props: Props) => {
  const [defaultFont, setDefaultFont] = useState<string>("openSans");
  const { children, router } = props;
  const [ctxTitle, setCtxTitle] = useState<string | JSX.Element>("");
  const { user } = useAppSelector((s) => s.user);

  const online = useOnlineStatus();

  const { data, isLoading } = useTokenRefreshQuery();

  const dispatch = useAppDispatch();

  useEffect(() => {
    const df = localStorage.getItem("defaultFont");
    if (df) {
      setDefaultFont(df);
    }
  }, []);

  useEffect(() => {
    const logrocketEnabled = process.env.NEXT_PUBLIC_LOGS;
    if (!isEmpty(user) && logrocketEnabled) {
      if (+user.logRocket! > 0) {
        LogRocket.init("gp2g0z/testapp");
        LogRocket.identify("gp2g0z/testapp", {
          name: user.email as string,
          email: user.email as string,
        });
      }
    }
  }, [user]);

  // Simple authentication effect
  useEffect(() => {
    if (isLoading) return; // Wait for token refresh to complete

    const accessToken = data?.accessToken || "";

    // const currentPath = router.asPath.split("?")[0];

    // Update Redux with current token state
    dispatch(setAccessToken({ accessToken }));
    dispatch(setAuthState({ authState: "IDLE" }));
  }, [data, isLoading, router, dispatch]);

  if (!online) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        h="100vh"
        direction="column"
        gap={5}
      >
        <Text fontWeight={500} fontSize={"3xl"}>
          You are offline!
        </Text>
        <OrangeButton size="lg" onClick={() => location.reload()}>
          Try again
        </OrangeButton>
      </Flex>
    );
  }

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" h="100vh">
        <Loader />
        <noscript>
          <Text fontWeight={500} fontSize={"3xl"}>
            Please enable JavaScript, or update your browser to use this app.
          </Text>
        </noscript>
      </Flex>
    );
  }

  let font = openSans;
  if (defaultFont === "lato") {
    font = lato;
  } else if (defaultFont === "inter") {
    font = inter;
  } else if (defaultFont === "roboto") {
    font = roboto;
  } else if (defaultFont === "openSans") {
    font = openSans;
  }

  return (
    <RootContext.Provider
      value={{ ctxTitle, defaultFont, setCtxTitle, setDefaultFont }}
    >
      <div className={`${font.className} antialiased`}>
        <DataLoader router={router} />
        {children}
      </div>
    </RootContext.Provider>
  );
};

export default AuthWrapper;
